import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Bank, Due, Hike, Media, Medium, Message, OrgChart, Particular, Payment, ProvidentFund, Salary, SalaryParticular, SalarySlip, Timeline } from '../models/domain';

@Injectable({
  providedIn: `root`,
})
export class EmployeeService {
  constructor(private httpClient: HttpClient) { }

  //MEDIUM API'S
  public getMediums(employee: number): Observable<Medium[]> {
    return this.httpClient.get<Medium[]>(`/api/medium/v1`, { params: { ...(employee && { employee }) } });
  }

  public getMedium(id: number): Observable<Medium> {
    return this.httpClient.get<Medium>(`/api/medium/v1/${id}`);
  }

  public createMedium(medium: Medium): Observable<Medium> {
    return this.httpClient.post<Medium>(`/api/medium/v1`, medium);
  }

  public updateMedium(id: number, medium: Medium): Observable<Medium> {
    return this.httpClient.put<Medium>(`/api/medium/v1/${id}`, medium);
  }

  public deleteMedium(id: number): Observable<Message> {
    return this.httpClient.delete<Message>(`/api/medium/v1/${id}`);
  }

  public getMedia(): Observable<Media[]> {
    return this.httpClient.get<Media[]>('/api/medium/v1/social');
  }

  public toggleSocialMediaStatus(medium: Medium): Observable<Medium> {
    return this.httpClient.put<Medium>(`/api/medium/v1/${medium.id}`, medium);
  }

  // TIMELINE API'S
  public getTimelines(employee: number): Observable<Timeline[]> {
    return this.httpClient.get<Timeline[]>(`/api/timeline/v1`, { params: { ...(employee && { employee }) } });
  }

  public getTimeline(id: number): Observable<Timeline> {
    return this.httpClient.get<Timeline>(`/api/timeline/v1/${id}`);
  }

  public createTimeline(timeline: Timeline): Observable<Timeline> {
    return this.httpClient.post<Timeline>(`/api/timeline/v1`, timeline);
  }

  public updateTimeline(id: number, timeline: Timeline): Observable<Timeline> {
    return this.httpClient.put<Timeline>(`/api/timeline/v1/${id}`, timeline);
  }

  public deleteTimeline(id: number): Observable<Message> {
    return this.httpClient.delete<Message>(`/api/timeline/v1/${id}`);
  }

  public toggleDesiginationStatus(timeline: Timeline): Observable<Timeline> {
    return this.httpClient.put<Timeline>(`/api/timeline/v1/${timeline.id}`, timeline);
  }

  public getNewTimeline(employeeId: number): Observable<Timeline> {
    return this.httpClient.get<Timeline>(`/api/timeline/v1/latest/${employeeId}`);
  }

  // BANK APIs
  public createBank(bank: Bank): Observable<Bank> {
    return this.httpClient.post<Bank>(`/api/bank/v1`, bank);
  }

  public getBanks(employee: number): Observable<Bank[]> {
    return this.httpClient.get<Bank[]>(`/api/bank/v1`, { params: { ...(employee && { employee }) } });;
  }

  public getBank(id: number): Observable<Bank> {
    return this.httpClient.get<Bank>(`/api/bank/v1/${id}`);
  }

  public updateBank(id: number, bank: Bank): Observable<Message> {
    return this.httpClient.put<Message>(`/api/bank/v1/${id}`, bank);
  }

  public deleteBank(id: number): Observable<Message> {
    return this.httpClient.delete<Message>(`/api/bank/v1/${id}`);
  }

  public toggleBankStatus(bank: Bank): Observable<Bank> {
    return this.httpClient.put<Bank>(`/api/bank/v1/${bank.id}`, bank);
  }

  // Salary APIs
  public createSalary(salary: Salary): Observable<Message> {
    return this.httpClient.post<Message>(`/api/payroll/salary`, salary);
  }

  public getSalary(id: number): Observable<Salary> {
    return this.httpClient.get<Salary>(`/api/payroll/salary/${id}`);
  }

  public getFrequency(): Observable<string[]> {
    return this.httpClient.get<string[]>(`/api/payroll/salary/frequency`);
  }

  public getSalaries(employee: number, disabled?: boolean, date?: string): Observable<Salary[]> {
    return this.httpClient.get<Salary[]>(`/api/payroll/salaries`, { params: { employee, ...(disabled != null && { disabled }), ...(date && { date }) } });
  }

  public checkSalary(id: number): Observable<boolean> {
    return this.httpClient.get<boolean>(`/api/payroll/salary/check/${id}`);
  }

  public updateSalary(id: number, salary: Salary): Observable<Message> {
    return this.httpClient.put<Message>(`/api/payroll/salary/${id}`, salary);
  }

  public toggleSalaryStatus(id: number, active: boolean): Observable<Message> {
    return this.httpClient.patch<Message>(`/api/payroll/salary-status/${id}?active=${active}`, {});
  }

  public createHike(employeeId: number, hike: number, fromDate: string): Observable<Hike> {
    return this.httpClient.post<Hike>(`/api/salary/v1/hike/${employeeId}?hike=${hike}&fromDate=${fromDate}`, {});
  }

  public dueCheck(salaryId: number, date: string): Observable<boolean> {
    return this.httpClient.get<boolean>(`/api/payroll/salary/due-check/${salaryId}/${date}`);
  }

  //Salary-particular
  public createSalaryParticular(salary: SalaryParticular): Observable<Message> {
    return this.httpClient.post<Message>(`/api/payroll/salary-particular`, salary);
  }

  public getSalaryParticulars(salaryId: number, disabled?: boolean): Observable<SalaryParticular[]> {
    return this.httpClient.get<SalaryParticular[]>(`/api/payroll/salary-particulars`, { params: { ...(salaryId && { salaryId }), ...(disabled != null && { disabled }) } }
    );
  }

  public getSalaryParticular(id: number): Observable<SalaryParticular> {
    return this.httpClient.get<SalaryParticular>(`/api/payroll/salary-particular/${id}`);
  }

  public updateSalaryParticular(id: number, salary: SalaryParticular): Observable<Message> {
    return this.httpClient.put<Message>(`/api/payroll/salary-particular/${id}`, salary);
  }

  public deleteSalaryParticular(id: number): Observable<Message> {
    return this.httpClient.delete<Message>(`/api/payroll/salary-particular/${id}`);
  }

  public getSalarySummary(salaryId: number, date: string): Observable<SalarySlip> {
    return this.httpClient.get<SalarySlip>(`/api/payroll/salary-summary/${salaryId}/${date}`);
  }

  //PAYMENT API'S
  public createPayment(payment: Payment): Observable<Message> {
    return this.httpClient.post<Message>(`/api/payroll/payment`, payment);
  }

  public getPayments(employeeId: number, salaryId?: number, fromDate?: string, toDate?: string, status?: string): Observable<Payment[]> {
    return this.httpClient.get<Payment[]>(`/api/payroll/payments?employeeId=${employeeId}`, { params: { ...(salaryId && { salaryId }), ...(fromDate && { fromDate }), ...(toDate && { toDate }), ...(status && { status }), } });
  }

  public getPayment(id: number): Observable<Payment> {
    return this.httpClient.get<Payment>(`/api/payroll/payment/${id}`);
  }

  public updatePayment(id: number, payment: Payment): Observable<Message> {
    return this.httpClient.put<Message>(`/api/payroll/payment/${id}`, payment);
  }

  public deletePayment(id: number): Observable<Message> {
    return this.httpClient.delete<Message>(`/api/payroll/payment/${id}`);
  }

  public getPaymentModes(): Observable<string[]> {
    return this.httpClient.get<string[]>('/api/payroll/payment-mode');
  }

  public togglePaymentStatus(payment: Payment): Observable<Payment> {
    return this.httpClient.put<Payment>(`/api/payroll/payment/${payment.id}`, payment);
  }

  public getSalarySlip(paymentId: number): Observable<SalarySlip> {
    return this.httpClient.get<SalarySlip>(`/api/payroll/salary-slip/${paymentId}`);
  }

  //PARTICULAR API'S
  public createParticular(particular: Particular): Observable<Particular> {
    return this.httpClient.post<Particular>(`/api/payroll/particular`, particular);
  }

  public getParticulars(earnings?: boolean, disabled?: boolean): Observable<Particular[]> {
    return this.httpClient.get<Particular[]>(`/api/payroll/particulars`, { params: { ...(disabled != null && { disabled }) }, ...(earnings != null && { earnings }), });
  }

  public getParticular(id: number): Observable<Particular> {
    return this.httpClient.get<Particular>(`/api/payroll/particular/${id}`)
  }

  public updateParticular(id: number, particular: Particular): Observable<Message> {
    return this.httpClient.put<Message>(`/api/payroll/particular/${id}`, particular);
  }

  public deleteParticular(id: number): Observable<Message> {
    return this.httpClient.delete<Message>(`/api/payroll/particular/${id}`);
  }

  public toggleParticularStatus(particular: Particular): Observable<Particular> {
    return this.httpClient.put<Particular>(`/api/payroll/particular/${particular.id}`, particular);
  }

  //PROVIDENT FUND API'S
  public createProvidentFund(providentFund: ProvidentFund): Observable<ProvidentFund> {
    return this.httpClient.post<ProvidentFund>(`/api/payroll/provident-fund`, providentFund);
  }

  public getProvidentFunds(disabled?: boolean): Observable<ProvidentFund[]> {
    return this.httpClient.get<ProvidentFund[]>(`/api/payroll/provident-funds`, { params: { ...(disabled != null && { disabled }) } });
  }

  public getProvidentFund(id: number): Observable<ProvidentFund> {
    return this.httpClient.get<ProvidentFund>(`/api/payroll/provident-fund/${id}`)
  }

  public updateProvidentFund(id: number, providentFund: ProvidentFund): Observable<Message> {
    return this.httpClient.put<Message>(`/api/payroll/provident-fund/${id}`, providentFund);
  }

  public deleteProvidentFund(id: number): Observable<Message> {
    return this.httpClient.delete<Message>(`/api/payroll/provident-fund/${id}`);
  }

  public toggleProvidentFundStatus(providentFund: ProvidentFund): Observable<ProvidentFund> {
    return this.httpClient.put<ProvidentFund>(`/api/payroll/provident-fund/${providentFund.id}`, providentFund);
  }

  public getTypes(): Observable<string[]> {
    return this.httpClient.get<string[]>(`/api/payroll/fund-type`)
  }

  // DUECHECK API'S
  public createDue(due: Due): Observable<Message> {
    return this.httpClient.post<Message>(`/api/payroll/payment`, due);
  }

  // ORGCHART API'S
  public getOrgChart(employeeId: number): Observable<OrgChart[]> {
    return this.httpClient.get<OrgChart[]>(`/api/payroll/org-chart/${employeeId}`);
  }

  //ACTIVE SALARY API'S
  public getActiveSalaries(date: string, frequency: string): Observable<Salary[]> {
    return this.httpClient.get<Salary[]>(`/api/payroll/active-salaries`, { params: { date, frequency } })
  }
}
